import React, { useEffect, useState, useCallback } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InputAdornment from '@mui/material/InputAdornment';
import useNotification from '../hooks/useNotification';
import Notification from './Notification';

// Reusing your FileDropOverlay component
import FileDropOverlay from './utils/FileDropOverlay';

const XmlTool = () => {
  const [inputText, setInputText] = useState('');
  const [parsedXmlDoc, setParsedXmlDoc] = useState(null);
  const [allUniqueTags, setAllUniqueTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [xmlParseError, setXmlParseError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const { snackbarOpen, snackbarMessage, handleCopy, showNotification, handleCloseSnackbar } = useNotification();

  // File Drag and Drop Handlers
  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        setInputText(event.target.result);
        showNotification(`File "${file.name}" loaded successfully!`);
      };

      reader.onerror = () => {
        showNotification(`Error reading file "${file.name}".`);
      };

      reader.readAsText(file);
    } else {
      showNotification('No file dropped or invalid drop operation.');
    }
  };

  // XML Parsing and Tag Extraction Logic
  const parseXmlAndExtractTags = useCallback(() => {
    if (!inputText.trim()) {
      setParsedXmlDoc(null);
      setAllUniqueTags([]);
      setSelectedTags([]);
      setXmlParseError('');
      return;
    }

    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(inputText, "application/xml");

      // Check for parsing errors
      const errorNode = xmlDoc.querySelector('parsererror');
      if (errorNode) {
        let errorMessage = errorNode.textContent;
        // Regex to find "Line Number X, Column Y:"
        const lineNumberMatch = errorMessage.match(/Line Number (\d+), Column (\d+):/);

        if (lineNumberMatch && lineNumberMatch[1] && lineNumberMatch[2]) {
          // If match found, use a cleaner message
          errorMessage = `XML Parsing Error: Line ${lineNumberMatch[1]}, Column ${lineNumberMatch[2]}.`;
        } else {
          // Fallback if the specific pattern isn't found, but still an error
          errorMessage = `XML Parsing Error: ${errorMessage.split('\n')[0].trim()}`; // Take only the first line
        }

        setXmlParseError(errorMessage);
        setParsedXmlDoc(null);
        setAllUniqueTags([]);
        setSelectedTags([]);
        return;
      }

      setParsedXmlDoc(xmlDoc);
      setXmlParseError('');

      // Extract all unique element tags in document order
      const tags = new Set();
      const allElements = xmlDoc.getElementsByTagName('*');
      for (let i = 0; i < allElements.length; i++) {
        tags.add(allElements[i].nodeName);
      }
      setAllUniqueTags(Array.from(tags));

    } catch (error) {
      setXmlParseError(`General XML Error: ${error.message}`);
      setParsedXmlDoc(null);
      setAllUniqueTags([]);
      setSelectedTags([]);
    }
  }, [inputText]);

  useEffect(() => {
    parseXmlAndExtractTags();
  }, [parseXmlAndExtractTags]);

  // --- Tag Selection and Filtering Logic ---
  const handleTagToggle = useCallback((tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  }, []);

  const filterAndDisplayResults = useCallback(() => {
    if (!parsedXmlDoc) {
      setFilteredResults([]);
      return;
    }

    const results = [];
    const tagsToSearchSet = new Set(selectedTags.length > 0 ? selectedTags : allUniqueTags);

    // Recursive function to traverse the DOM and collect matching elements
    const traverseAndCollect = (node, level) => {
      if (node.nodeType === 1) { // Element node
        if (tagsToSearchSet.has(node.nodeName)) {
          results.push({
            tag: node.nodeName,
            content: node.textContent.trim(),
            attributes: Array.from(node.attributes).map(attr => `${attr.name}="${attr.value}"`).join(' '),
            path: getElementPath(node),
            level: level // Store the nesting level for indentation
          });
        }
        // Iterate through child elements (not just child nodes)
        for (let i = 0; i < node.children.length; i++) {
          traverseAndCollect(node.children[i], level + 1);
        }
      }
    };

    // Start traversal from the document's root element
    if (parsedXmlDoc.documentElement) {
      traverseAndCollect(parsedXmlDoc.documentElement, 0); // Start at level 0
    }

    setFilteredResults(results);
  }, [parsedXmlDoc, selectedTags, allUniqueTags]);

  // Effect to filter and display results when parsed XML or selected tags change
  useEffect(() => {
    filterAndDisplayResults();
  }, [filterAndDisplayResults]);

  // Helper function to get a simple XPath-like path for an element
  const getElementPath = (element) => {
    if (!element || element.nodeType !== 1) return '';
    let path = element.nodeName;
    let parent = element.parentNode;
    while (parent && parent.nodeType === 1) {
      path = `${parent.nodeName}/${path}`;
      parent = parent.parentNode;
    }
    return path;
  };

  return (
    <Grid container spacing={4} direction="column">
      {/* Input Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginBottom: 1,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'auto',
            }}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            {isDragging && <FileDropOverlay />}
            <TextField
              label={
                !isDragging && inputText.length
                  ? `XML Input`
                  : !isDragging && "Input XML (Drag & drop file or type/paste XML)"
              }
              multiline
              rows={8}
              variant="outlined"
              fullWidth
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={
                !isDragging && `Input XML here or drag & drop an XML file\n\n<root>\n  <item id="1">Value 1</item>\n  <item id="2">Value 2</item>\n</root>`
              }
              sx={{
                borderRadius: 1,
                '& .MuiInputBase-input': { paddingRight: '45px' }
              }}
              slotProps={{
                input: {
                  endAdornment: inputText.length > 0 && (
                    <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                      <IconButton onClick={() => handleCopy(inputText, 'Input copied to clipboard!')} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          {xmlParseError && (
            <Typography
              color="error"
              variant="body2"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 1
              }}
            >
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 1, width: 'auto' }}>
                {xmlParseError}
              </Alert>
            </Typography>
          )}
        </Box>
      </Grid>

      {/* Tag Selection Section */}
      {allUniqueTags.length > 0 && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ padding: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Tags to Display:
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
              {allUniqueTags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  onClick={() => handleTagToggle(tag)}
                  color={selectedTags.includes(tag) ? 'primary' : 'default'}
                  variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                  clickable
                />
              ))}
              {selectedTags.length > 0 && (
                <Button
                  onClick={() => setSelectedTags([])}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  Clear Selection
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      )}

      {/* Output Display Section */}
      {parsedXmlDoc && (
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ padding: 2, borderRadius: 2, minHeight: '150px', maxHeight: '500px', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Extracted Tag Content:
            </Typography>
            {filteredResults.length > 0 ? (
              filteredResults.map((result, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 1,
                    p: 1,
                    borderBottom: '1px dashed #eee',
                    ml: result.level * 2, // Apply left margin based on nesting level (e.g., 2 units per level)
                    backgroundColor: result.level % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.02)', // Subtle zebra striping
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    Path: {result.path}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {result.tag}
                    {result.attributes && ` (${result.attributes})`}
                  </Typography>
                  <Typography variant="body1" color="primary" sx={{ ml: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {result.content || (result.attributes ? '' : '[No Text Content]')}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                {parsedXmlDoc ? 'Select tags above to view content.' : 'Parse XML to see tags and content here.'}
              </Typography>
            )}
          </Paper>
        </Grid>
      )}

      <Notification
        open={snackbarOpen}
        message={snackbarMessage}
        handleClose={handleCloseSnackbar}
      />
    </Grid>
  );
};

export default XmlTool;